import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Tab, TabList, TabPanel, Tabs } from '../../../../CommonComponents/OPL/Tab';
import { PersonalInfoForm } from '../PersonalInfoForm/PersonalInfoForm';
import { Card } from '../../../../CommonComponents/OPL/Card/Card';
import {
  EstatePlanningDTO,
  EstatePlanningOptionsDTO,
} from '../../api/useEstatePlanningPersonalInfoQuery';
import { PersonalInfoDTO } from '../../api/usePersonalInfoQuery';
import { TrustsDTO } from '../../api/useTrustsQuery';
import { Separator } from '../../../../CommonComponents/OPL/Separator/Separator';
import { Trusts } from '../Trusts/Trusts';
import { FORM_ID, FormValues, PersonalInfoContext } from '../../PersonalInfo.meta';
import { Deductions } from '../Deductions/Deductions';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';

const ERROR_PER_TAB_MAP = {
  personalInfo: 0,
  deductionsAndGifts: 1,
} as Record<keyof FormValues, number>;

interface Props {
  isEstatePlanning?: boolean;
  data: {
    EstatePlanning?: EstatePlanningDTO;
    PersonalInfo: PersonalInfoDTO;
    EstatePlanningOptions?: EstatePlanningOptionsDTO;
    Trusts?: TrustsDTO;
  };
  showPartnerSection: boolean;
  setShowPartnerSection(showPartnerSection: boolean): void;
  onAddPartner(): void;
  onDeletePartner(): void;
  submitPersonalInfo(): void;
  resetPersonalInfo(data: PersonalInfoDTO): void;
  onSubmit(): void;
  context?: PersonalInfoContext;
}

export const FormWrapper = ({
  data,
  onAddPartner,
  onDeletePartner,
  onSubmit,
  resetPersonalInfo,
  showPartnerSection,
  setShowPartnerSection,
  isEstatePlanning,
  submitPersonalInfo,
  context,
}: Props) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    formState: { errors },
  } = useFormContext<FormValues>();

  useEffect(() => {
    if (isEstatePlanning) {
      const errorsKeys = Object.keys(errors);

      if (errorsKeys.length > 0) {
        setSelectedTab(ERROR_PER_TAB_MAP[errorsKeys[0]]);
      }
    }
  }, [errors, isEstatePlanning]);

  if (isEstatePlanning && data.EstatePlanningOptions) {
    return (
      <>
        <form id={FORM_ID} aria-label="personal info form" onSubmit={onSubmit}>
          <Tabs selectedIndex={selectedTab} onSelect={index => setSelectedTab(index)}>
            <TabList>
              <Tab>Household Info</Tab>
              <Tab>Deductions & Gifts</Tab>
            </TabList>

            <TabPanel>
              <Card p={32} mt={32}>
                <PersonalInfoForm
                  data={data}
                  showPartnerSection={showPartnerSection}
                  setShowPartnerSection={setShowPartnerSection}
                  onAddPartner={onAddPartner}
                  onDeletePartner={onDeletePartner}
                  isEstatePlanning={isEstatePlanning}
                />
              </Card>
            </TabPanel>

            <TabPanel>
              <Card p={32} mt={32}>
                <Deductions
                  showPartnerSection={showPartnerSection}
                  principalLifetimeGiftingExemptionAmount={
                    data.EstatePlanningOptions.PrimaryEstateOptions
                      ?.BaseLifetimeGiftingExemptionAmount
                  }
                  spouseLifetimeGiftingExemptionAmount={
                    data.EstatePlanningOptions.SpouseEstateOptions
                      ?.BaseLifetimeGiftingExemptionAmount
                  }
                  addUnusedExemption={data.EstatePlanningOptions.AddUnusedExemption}
                />
              </Card>
            </TabPanel>
          </Tabs>
        </form>

        {selectedTab === 0 && (
          <Card p={32} pb={32} css="position:relative">
            {/* Make trusts look like is inside personal info card*/}
            <Box
              css="position:absolute; height: 32px; width: 100%; top: -16px; left:0; z-index: 2"
              bg="bg"
            />

            <Separator mb="32" />

            <Trusts
              data={data.Trusts}
              submitPersonalInfo={submitPersonalInfo}
              resetPersonalInfo={resetPersonalInfo}
              isEstatePlanning={isEstatePlanning}
            />
          </Card>
        )}
      </>
    );
  }

  return (
    <>
      <form id={FORM_ID} aria-label="personal info form" onSubmit={onSubmit}>
        <PersonalInfoForm
          data={data}
          showPartnerSection={showPartnerSection}
          setShowPartnerSection={setShowPartnerSection}
          onAddPartner={onAddPartner}
          onDeletePartner={onDeletePartner}
        />
      </form>

      {context !== PersonalInfoContext.CharitableGivingWorkflow && (
        <>
          <Separator my="32" />

          <Trusts
            data={data.Trusts}
            submitPersonalInfo={submitPersonalInfo}
            resetPersonalInfo={resetPersonalInfo}
          />
        </>
      )}
    </>
  );
};
